import { useState } from "react";
import { useEscape } from "../hooks/useEscape.js";

// Points to mm conversion
function pt2mm(pt: number): number {
  return (pt * 25.4) / 72;
}

export type StaticSchemaEntry = {
  name: string;
  type: "text";
  content: string;
  position: { x: number; y: number };
  width: number;
  height: number;
  fontSize: number;
  fontColor: string;
  alignment: "left" | "center" | "right";
  readOnly: true;
  opacity: number;
};

interface Props {
  entries: StaticSchemaEntry[];
  pageWidthPt: number;
  pageHeightPt: number;
  onChange: (entries: StaticSchemaEntry[]) => void;
  onClose: () => void;
}

type EntryForm = {
  name: string;
  content: string;
  x: string;
  y: string;
  width: string;
  height: string;
  fontSize: string;
  fontColor: string;
  alignment: "left" | "center" | "right";
};

const EMPTY_FORM: EntryForm = {
  name: "",
  content: "",
  x: "5",
  y: "2",
  width: "50",
  height: "6",
  fontSize: "10",
  fontColor: "#000000",
  alignment: "left",
};

type Preset = {
  label: string;
  getValues: (wMm: number, hMm: number) => Partial<EntryForm>;
};

const MARGIN = 5; // mm from page edge
const BAND_HEIGHT = 6; // mm

// Presets fill the full usable width and use text-alignment for Left/Center/Right.
// This works correctly for any page size.
function makePresets(): Preset[] {
  return [
    {
      label: "Header Left",
      getValues: (w) => ({
        x: String(MARGIN),
        y: "2",
        width: String((w - MARGIN * 2).toFixed(1)),
        height: String(BAND_HEIGHT),
        alignment: "left" as const,
      }),
    },
    {
      label: "Header Center",
      getValues: (w) => ({
        x: String(MARGIN),
        y: "2",
        width: String((w - MARGIN * 2).toFixed(1)),
        height: String(BAND_HEIGHT),
        alignment: "center" as const,
      }),
    },
    {
      label: "Header Right",
      getValues: (w) => ({
        x: String(MARGIN),
        y: "2",
        width: String((w - MARGIN * 2).toFixed(1)),
        height: String(BAND_HEIGHT),
        alignment: "right" as const,
      }),
    },
    {
      label: "Footer Left",
      getValues: (w, h) => ({
        x: String(MARGIN),
        y: String((h - BAND_HEIGHT - 2).toFixed(1)),
        width: String((w - MARGIN * 2).toFixed(1)),
        height: String(BAND_HEIGHT),
        alignment: "left" as const,
      }),
    },
    {
      label: "Footer Center",
      getValues: (w, h) => ({
        x: String(MARGIN),
        y: String((h - BAND_HEIGHT - 2).toFixed(1)),
        width: String((w - MARGIN * 2).toFixed(1)),
        height: String(BAND_HEIGHT),
        alignment: "center" as const,
      }),
    },
    {
      label: "Footer Right",
      getValues: (w, h) => ({
        x: String(MARGIN),
        y: String((h - BAND_HEIGHT - 2).toFixed(1)),
        width: String((w - MARGIN * 2).toFixed(1)),
        height: String(BAND_HEIGHT),
        alignment: "right" as const,
      }),
    },
  ];
}

function formToEntry(form: EntryForm): StaticSchemaEntry | null {
  const x = parseFloat(form.x);
  const y = parseFloat(form.y);
  const width = parseFloat(form.width);
  const height = parseFloat(form.height);
  const fontSize = parseFloat(form.fontSize);
  if (
    !form.name.trim() ||
    isNaN(x) ||
    isNaN(y) ||
    isNaN(width) ||
    isNaN(height) ||
    isNaN(fontSize)
  ) {
    return null;
  }
  return {
    name: form.name.trim(),
    type: "text",
    content: form.content,
    position: { x, y },
    width,
    height,
    fontSize,
    fontColor: form.fontColor,
    alignment: form.alignment,
    readOnly: true,
    opacity: 1,
  };
}

function entryToForm(entry: StaticSchemaEntry): EntryForm {
  return {
    name: entry.name,
    content: entry.content,
    x: String(entry.position.x),
    y: String(entry.position.y),
    width: String(entry.width),
    height: String(entry.height),
    fontSize: String(entry.fontSize),
    fontColor: entry.fontColor,
    alignment: entry.alignment,
  };
}

export function HeaderFooterDialog({
  entries,
  pageWidthPt,
  pageHeightPt,
  onChange,
  onClose,
}: Props) {
  useEscape(onClose);
  const [form, setForm] = useState<EntryForm>(EMPTY_FORM);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const wMm = pt2mm(pageWidthPt);
  const hMm = pt2mm(pageHeightPt);
  const presets = makePresets();

  function setField<K extends keyof EntryForm>(key: K, value: EntryForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function applyPreset(preset: Preset) {
    const vals = preset.getValues(wMm, hMm);
    setForm((prev) => ({ ...prev, ...vals }));
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs["name"] = "Name is required";
    if (isNaN(parseFloat(form.x))) errs["x"] = "Invalid number";
    if (isNaN(parseFloat(form.y))) errs["y"] = "Invalid number";
    if (isNaN(parseFloat(form.width)) || parseFloat(form.width) <= 0)
      errs["width"] = "Must be > 0";
    if (isNaN(parseFloat(form.height)) || parseFloat(form.height) <= 0)
      errs["height"] = "Must be > 0";
    if (isNaN(parseFloat(form.fontSize)) || parseFloat(form.fontSize) <= 0)
      errs["fontSize"] = "Must be > 0";

    // Check uniqueness (excluding current edit)
    const isDuplicate = entries.some(
      (e, idx) =>
        e.name === form.name.trim() &&
        (editingIndex === null || idx !== editingIndex),
    );
    if (isDuplicate) errs["name"] = "Name must be unique";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleAddOrUpdate() {
    if (!validate()) return;
    const entry = formToEntry(form);
    if (!entry) return;

    if (editingIndex !== null) {
      const updated = entries.map((e, i) => (i === editingIndex ? entry : e));
      onChange(updated);
    } else {
      onChange([...entries, entry]);
    }

    setForm(EMPTY_FORM);
    setEditingIndex(null);
    setErrors({});
  }

  function handleEdit(index: number) {
    setEditingIndex(index);
    setForm(entryToForm(entries[index]));
    setErrors({});
  }

  function handleDelete(index: number) {
    onChange(entries.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
      setForm(EMPTY_FORM);
    }
  }

  function handleCancelEdit() {
    setEditingIndex(null);
    setForm(EMPTY_FORM);
    setErrors({});
  }

  return (
    <div
      className="dialog-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="dialog dialog--wide hf-dialog">
        <h3>Header &amp; Footer</h3>
        <p className="dialog__subtitle">
          Static elements rendered on every page. Use{" "}
          <code>{"{currentPage}"}</code>, <code>{"{totalPages}"}</code>,{" "}
          <code>{"{date}"}</code>, or <code>{"{dateTime}"}</code> in content.
        </p>

        {/* Existing entries list */}
        {entries.length > 0 && (
          <div className="hf-dialog__list">
            {entries.map((entry, i) => (
              <div
                key={entry.name}
                className={`hf-dialog__entry${editingIndex === i ? " hf-dialog__entry--active" : ""}`}
              >
                <div className="hf-dialog__entry-info">
                  <span className="hf-dialog__entry-name">{entry.name}</span>
                  <span className="hf-dialog__entry-content">
                    {entry.content || <em>empty</em>}
                  </span>
                  <span className="hf-dialog__entry-pos">
                    x:{entry.position.x} y:{entry.position.y} · {entry.width}×
                    {entry.height}mm · {entry.fontSize}pt
                  </span>
                </div>
                <div className="hf-dialog__entry-actions">
                  <button
                    className="btn btn--sm btn--ghost"
                    onClick={() => handleEdit(i)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn--sm btn--ghost hf-dialog__delete-btn"
                    onClick={() => handleDelete(i)}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Form */}
        <div className="hf-dialog__form">
          <h4>{editingIndex !== null ? "Edit Entry" : "Add Entry"}</h4>

          {/* Presets */}
          <div className="hf-dialog__presets">
            <span className="hf-dialog__presets-label">Position preset:</span>
            {presets.map((p) => (
              <button
                key={p.label}
                className="btn btn--sm btn--ghost"
                onClick={() => applyPreset(p)}
                type="button"
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g. pageNumber"
              />
              {errors["name"] && (
                <span className="form-error">{errors["name"]}</span>
              )}
            </div>
            <div className="form-group">
              <label>Alignment</label>
              <select
                value={form.alignment}
                onChange={(e) =>
                  setField(
                    "alignment",
                    e.target.value as "left" | "center" | "right",
                  )
                }
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Content</label>
            <input
              value={form.content}
              onChange={(e) => setField("content", e.target.value)}
              placeholder="e.g. Page {currentPage} of {totalPages}"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>X (mm) *</label>
              <input
                type="number"
                value={form.x}
                onChange={(e) => setField("x", e.target.value)}
                step="0.1"
              />
              {errors["x"] && <span className="form-error">{errors["x"]}</span>}
            </div>
            <div className="form-group">
              <label>Y (mm) *</label>
              <input
                type="number"
                value={form.y}
                onChange={(e) => setField("y", e.target.value)}
                step="0.1"
              />
              {errors["y"] && <span className="form-error">{errors["y"]}</span>}
            </div>
            <div className="form-group">
              <label>Width (mm) *</label>
              <input
                type="number"
                value={form.width}
                onChange={(e) => setField("width", e.target.value)}
                step="0.1"
                min="1"
              />
              {errors["width"] && (
                <span className="form-error">{errors["width"]}</span>
              )}
            </div>
            <div className="form-group">
              <label>Height (mm) *</label>
              <input
                type="number"
                value={form.height}
                onChange={(e) => setField("height", e.target.value)}
                step="0.1"
                min="1"
              />
              {errors["height"] && (
                <span className="form-error">{errors["height"]}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Font Size (pt) *</label>
              <input
                type="number"
                value={form.fontSize}
                onChange={(e) => setField("fontSize", e.target.value)}
                step="0.5"
                min="1"
              />
              {errors["fontSize"] && (
                <span className="form-error">{errors["fontSize"]}</span>
              )}
            </div>
            <div className="form-group">
              <label>Font Color</label>
              <div className="hf-dialog__color-row">
                <input
                  type="color"
                  value={form.fontColor}
                  onChange={(e) => setField("fontColor", e.target.value)}
                  className="hf-dialog__color-input"
                />
                <span>{form.fontColor}</span>
              </div>
            </div>
          </div>

          <div className="hf-dialog__form-actions">
            <button
              className="btn btn--primary"
              onClick={handleAddOrUpdate}
              type="button"
            >
              {editingIndex !== null ? "Update" : "Add"}
            </button>
            {editingIndex !== null && (
              <button className="btn" onClick={handleCancelEdit} type="button">
                Cancel
              </button>
            )}
          </div>
        </div>

        <div className="dialog__actions">
          <button className="btn btn--primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
